import React, { FC, useCallback, useEffect, useState } from 'react';

import { ERequestStatus } from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { TField, TFieldNames } from '@r1-frontend/ui-react/organisms/Forms/NamePhoneForm';
import { useOnceExecute } from '@r1-frontend/shared/hooks/useOnceExecute';

import PopupResult from '~/src/components/Popups/PopupResult';
import { POLICY_PAGE_LINK, RECAPTCHA_SITE_KEY } from '~/src/constants';

import * as ST from './styled';

export type { TField };

export interface ISubmitParams {
    fio?: string,
    phone: string,
}

export interface ISubmitResult {
    ok: boolean,
    message: string,
    status: ERequestStatus,
}

export type TSubmitFunc = (params: ISubmitParams) => Promise<ISubmitResult> | null | void;

export interface IPropsRequestFormPhone {
    formTitle?: string,
    btnText: string | React.ReactNode,
    btnType?: btnTypes,
    isOnlyPhone?: boolean,
    isLoading?: boolean,
    isMobile?: boolean,
    isCheckInvertColor?: boolean,
    templateVariant?: ST.TTemplate,
    className?: string,
    submitRequest: TSubmitFunc,
    onStartFormFilling?: () => void,
    withResultPopup?: boolean,
}

/**
 *  Форма заказа обратного звонка, с попапом результата (отправку аналитики выполняем снаружи формы)
 * @param {boolean} isLoading - отображение спинера загрузки
 * @param {boolean} isMobile - принудительная установка мобильного вида формы (если форма занимает не всю ширину экрана)
 * @param {boolean} isPromoResultPopup - выводить промоблок в PopupResult
 * @param {function} submitRequest - колбэк при клике на кнопку
 * @param btnText
 * @param onStartFormFilling - событие начала заполнения формы
 */
export const RequestFormPhone:FC<IPropsRequestFormPhone> = ({
    formTitle,
    btnText,
    btnType = btnTypes.ACCENT,
    isMobile,
    templateVariant,
    isCheckInvertColor,
    className,
    submitRequest,
    onStartFormFilling,
    withResultPopup = true,
    isOnlyPhone = true,
    children,
}) => {
    const [isRecaptcha, setIsRecaptcha] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fio, setFio] = useState<TField>();
    const [phone, setPhone] = useState<TField>();
    const [popup, setPopup] = useState({
        isResultOpened: false,
        resultTitle: '',
        resultText: '',
        resultType: EResultPopupStatus.SUCCESS,
    });

    useEffect(() => {
        const href = window?.location?.href;
        setIsRecaptcha(href ? href.includes('recaptcha') : false);
    }, []);

    const firstInput = useOnceExecute(() => {
        if (typeof onStartFormFilling === 'function') {
            onStartFormFilling();
        }
    });

    const openPopupResult = useCallback((ok: boolean, message: string, status: EResultPopupStatus) => {
        setPopup({
            isResultOpened: true,
            resultType: status,
            resultTitle: ok
            ? templateVariant === 'second' ? 'Заявка отправлена' : 'Заявка на подключение отправлена'
            : 'Произошла ошибка',
            resultText: ok ? 'Мы свяжемся с вами в ближайшее время' : message,
        });
    }, [templateVariant]);

    const submitHandler = useCallback(async() => {
        if (phone?.isValid) {
            const params: ISubmitParams = {
                phone: phone?.value || '',
            };

            if (!isOnlyPhone && fio?.isValid) {
                params.fio = fio?.value || '';
            }

            setIsLoading(true);

            const res = await submitRequest(params);

            if (res) {
                const { ok, message, status } = res;
                openPopupResult(ok, message, status as unknown as EResultPopupStatus);
            }

            fio?.clearInput();
            phone?.clearInput();
            setFio(undefined);
            setPhone(undefined);
            setIsLoading(false);
        }
    }, [openPopupResult, fio, phone, isOnlyPhone, submitRequest]);

    const onChangeInput = useCallback((data: TField, type: TFieldNames) => {
        if (type === 'phone') {
            setPhone(data);
        } else {
            setFio(data);
        }

        if (data?.value && onStartFormFilling) {
            firstInput();
        }
    }, [firstInput, onStartFormFilling]);

    const closePopup = useCallback(() => {
        setPopup(state=>({
            ...state,
            isResultOpened: false,
        }));
    }, []);

    return (
        <ST.RequestFormWrapper className={className} isMobile={isMobile}>
            {formTitle &&
                <ST.Title
                    isMobile={isMobile}
                    dangerouslySetInnerHTML={{ __html: formTitle }}
                />
            }
            <ST.SNamePhoneForm
                isOnlyPhone={isOnlyPhone}
                templateVariant={isMobile ? 'mobile' : 'first'}
                btnText={btnText}
                btnType={btnType}
                onButtonClick={submitHandler}
                onChange={onChangeInput}
                isLoading={isLoading}
                invertColor={isCheckInvertColor}
                recapchaSiteKey={isRecaptcha ? RECAPTCHA_SITE_KEY : undefined}
                policyPath={POLICY_PAGE_LINK}
                className={'ym-hide-content'}
            />

            {children}

            {withResultPopup && <PopupResult
                isOpen={popup.isResultOpened}
                onClose={closePopup}
                title={popup.resultTitle}
                text={popup.resultText}
                type={popup.resultType}
                data-test='popup-result'
            />}
        </ST.RequestFormWrapper>
    );
};
